import ScrollModal from "@/components/common/scroll-modal";
import { DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { newParentFlowSchema } from "@/schemas/processes";
import { Button, Icon, TextField } from "@elaniintc/goes-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

type ApiErrors = {
  name?: string[];
};

interface ApiErrorResponse {
  errors?: ApiErrors;
}

interface NewFlowModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; extension_count: number }) => void;
  isLoading?: boolean;
  error?: AxiosError<ApiErrorResponse>;
}

const initialState = {
  name: "",
  extension_count: 0,
};

export default function NewParentFlowModal({
  open,
  onClose,
  onSubmit,
  isLoading = false,
  error,
}: NewFlowModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isValid },
  } = useForm<{ name: string; extension_count: number }>({
    resolver: zodResolver(newParentFlowSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: initialState,
  });

  const handleCloseModal = () => {
    reset(initialState);
    onClose();
  };

  useEffect(() => {
    const serverNameErr = error?.response?.data?.errors?.name;
    if (serverNameErr?.length) {
      setError("name", { type: "server", message: serverNameErr[0] });
    }
  }, [error, setError]);

  return (
    <ScrollModal
      isOpen={open}
      onClose={handleCloseModal}
      title="Crear Flujo principal"
      description="Este flujo es iniciado por el tramitadór, al cual todos los flujos secundarios estarán conectados."
      srOnlyDescription={false}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col gap-0"
      modalChildrenClassName="p-modal-md"
      header={
        <div className="flex items-center justify-between px-8 py-3">
          <DialogTitle>Crear flujo principal</DialogTitle>
          <DialogDescription className="sr-only">
            Formulario para editar una ficha técnica
          </DialogDescription>
          <Button
            type="button"
            onClick={onClose}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" />
          </Button>
        </div>
      }
      footer={
        <div className="w-full grid grid-cols-1 tablet:grid-cols-2 py-3 px-8 gap-3">
          <Button
            variant="tertiary"
            size="lg"
            type="button"
            disabled={isLoading}
            onClick={handleCloseModal}
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="lg"
            type="submit"
            disabled={!isValid}
            isLoading={isLoading}
            form="new-parent-flow-form"
          >
            Crear flujo
          </Button>
        </div>
      }
    >
      <div className="flex flex-col">
        <form
          id="new-parent-flow-form"
          onSubmit={handleSubmit((data) => {
            onSubmit(data);
          })}
          className="flex flex-col flex-1 min-h-0 gap-8"
        >
          <TextField
            label="Nombre del flujo"
            required
            error={errors.name?.message}
            placeholder="Escribe el nombre del flujo"
            {...register("name")}
          />
          <TextField
            label="Cantidad de prórrogas permitidas para el tramitador"
            required
            type="number"
            error={errors.extension_count?.message}
            placeholder="Escribe el número de prórrogas permitidas"
            {...register("extension_count", {
              valueAsNumber: true,
            })}
            helperText="Estas prórrogas pueden ser aplicadas en nodos que tengan vencimiento."
          />
        </form>
      </div>
    </ScrollModal>
  );
}
